import { useCallback, useMemo, useState } from 'react'
import { PhoneOff, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchPhoneBlocks,
  createPhoneBlock,
  removePhoneBlock,
  BAN_STR as S,
  type PhoneBlockRow,
} from './strikesApi'

type Kind = PhoneBlockRow['kind']
type Source = PhoneBlockRow['source']
const KINDS: { value: Kind; label: string }[] = [
  { value: 'number', label: S.phone },
  { value: 'prefix', label: S.sigPrefix },
]
const SOURCES: { value: Source; label: string }[] = [
  { value: 'manual', label: S.srcManual },
  { value: 'auto', label: S.srcAuto },
  { value: 'fraud', label: S.srcFraud },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/**
 * A8.17 — Telefon raqam bloklari. JONLI `/admin/phone-blocks` (000245): raqam/
 * prefiks qora ro'yxati (manual/auto/antifrod) — inline qo'shish ((kind,value)
 * bo'yicha idempotent) + o'chirish.
 */
export function PhoneBlocksScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [kind, setKind] = useState<Kind>('number')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [source, setSource] = useState<Source>('manual')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!value.trim()) return
    setBusy(true)
    try {
      await createPhoneBlock({ kind, value: value.trim(), reason: reason.trim(), source })
      toast(S.addOk, { tone: 'success' })
      setValue('')
      setReason('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<PhoneBlockRow>[]>(
    () => [
      {
        key: 'kind',
        header: S.blockKind,
        type: 'enum',
        width: 110,
        options: [
          { value: 'number', label: S.phone, status: 'review' },
          { value: 'prefix', label: S.sigPrefix, status: 'pending' },
        ],
      },
      { key: 'value', header: S.value, type: 'text', width: 180 },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      {
        key: 'source',
        header: S.source,
        type: 'enum',
        width: 130,
        options: [
          { value: 'manual', label: S.srcManual, status: 'review' },
          { value: 'auto', label: S.srcAuto, status: 'pending' },
          { value: 'fraud', label: S.srcFraud, status: 'banned' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<PhoneBlockRow>[]>(
    () => [
      {
        id: 'remove',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await removePhoneBlock(r.id)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchPhoneBlocks, [])
  return (
    <>
      <PageHeader title={S.phoneBlocks} screenId="A8.17" description={S.phoneBlocksNote} />
      <div className="flex flex-wrap items-end gap-2 p-4 pb-0">
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} className={inputCls}>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={S.phone}
          spellCheck={false}
          className={`${inputCls} w-44 font-mono`}
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={S.reason}
          className={`${inputCls} min-w-[180px] flex-1`}
        />
        <select value={source} onChange={(e) => setSource(e.target.value as Source)} className={inputCls}>
          {SOURCES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={add}
          disabled={busy || !value.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<PhoneBlockRow>
        key={reloadKey}
        screenId="A8.17"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <PhoneOff size={28} strokeWidth={1.5} />, title: S.phoneBlocks }}
      />
    </>
  )
}
